import { useToast } from "contexts/ToastContext";
import { useEffect, useRef, useState } from "react";
import { LoadingPageIcon } from "utils/constant";
import { useTranslation } from "react-i18next";

import { useParams } from "react-router-dom";
import { getStakeholder } from "services/getEmployees";
import LegislativeDraftDetail from "./fragments/leftPanel";
import { getLegislativeDrafting } from "services/legislativeService";
import LegislativeDraftRightPanel from "./fragments/rightPanel";
import { useAuth } from "contexts/AuthContext";
import RegistrarLegislativeDraft from "./registrarLegislativeDraft";
import { CanShow, LEGISLATIVE_DRAFTING_UI_RULES, TeamScope } from "utils/authRules";

import { formatDate } from "utils/helpers";
import FileView from "components/fileView";
import DocumentViewerModal from "components/DocumentViewer";

const SingleLegislativeDraft = () => {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const [data, setData] = useState({
        isLoading: true,
        current_status: "",
        forward_status: "",
        messenger_name: "",
        letter_reference_number: "",
        uploaded_files: [] as any[],
        research_completion_letter_file: "",
        is_published: false,
    });
    const { id } = useParams<{ id: string }>();
    const contextData = useAuth();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getLegislativeDrafting(id || "");
                let user = null;
                if (response.requested_by_id && response.requested_by !== "internal") {
                    try {
                        user = await getStakeholder(response.requested_by_id, contextData.token || contextData?.user?.access_token || "");
                    } catch (err) {
                        console.error("Error fetching stakeholder:", err);
                        showToast(t("FailedToFetchStakeholder") || "Failed to fetch stakeholder", "error");
                    }
                }
                setData({
                    isLoading: false,
                    ...response,
                    requested_by_user: user,
                    is_published: response.is_published || false,
                });
            } catch (error) {
                console.error("Error fetching legal research request:", error);
                showToast(t("FailedToFetchLegalResearchRequest") || "Failed to fetch legal research request.", "error");
                setData((prev) => ({ ...prev, isLoading: false }));
            }
        };

        fetchData();
    }, [id, t, showToast, contextData.token, contextData?.user?.access_token]);

    if (data.isLoading)
        return (
            <div className="flex justify-center items-center h-64">
                {LoadingPageIcon}
            </div>
        );

    // Prosecutor actions on this request go to its team leader only.
    return (
        <TeamScope team={(data as any)?.prosecutor_assignments}>
            <div className="h-full">
                <div className="flex pl-4 mb-4 gap-2 font-semibold text-2xl text-[#696969]">
                    <svg className="my-auto" xmlns="http://www.w3.org/2000/svg" width="24" height="20" viewBox="0 0 24 20" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M0.335938 10.815C3.32099 11.9763 6.47113 12.6575 9.66929 12.8334V15.1666H14.3359V12.8334C17.5264 12.6335 20.6706 11.9693 23.6693 10.8616V19.8334H0.335938V10.815ZM15.5026 0L16.6693 1.16665V3.5H23.6693V9.57835C20.0921 10.914 16.3107 11.6205 12.4926 11.6666H11.6526C7.78322 11.6248 3.95185 10.898 0.335938 9.52V3.5H7.33594V1.16665L8.50259 0H15.5026ZM14.3359 2.33335H9.66929V3.5H14.3359V2.33335Z" fill="#696969" />
                    </svg>
                    <h1>{t("ConductLegislativeRequest") || "Conduct Legislative Request"}</h1>
                </div>
                <div className="flex h-full bg-gray-100 p-2">
                    <LegislativeDraftDetail request={data} />
                    {CanShow(LEGISLATIVE_DRAFTING_UI_RULES.edit_reference_number.value, data?.current_status, data?.forward_status) ?
                        <RegistrarLegislativeDraft request={data} /> :
                        contextData.user.roles?.some((role: any) => role === "Registrar") ?
                            <div className="flex-1 h-fit bg-white mb-4 p-4 rounded-lg">
                                <div className="pb-2 mb-4">
                                    <h1>{t("MessengerDetail") || "Messenger Detail"}</h1>
                                </div>
                                {data?.uploaded_files?.find((document: any) => document.document_type === "research_completion_letter") &&
                                    <DocumentViewerModal
                                        documents={data?.uploaded_files.filter((document: any) => document.document_type === "research_completion_letter")}
                                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                    />
                                }
                            </div>
                            :
                            <LegislativeDraftRightPanel request={data} />
                    }
                </div>
            </div>
        </TeamScope>
    );
};

export default SingleLegislativeDraft;